import { useEffect } from 'react';
import { useCollectionStore } from '@stores/useCollectionStore';
import { filterFeatures } from '../utils/transformUtils';

/**
 * Derives the filtered GeoJSON features from the full (3857) dataset and the
 * current filter state, then writes the result to the Zustand store for the
 * map layer.
 *
 * The filtered set is a function of *applied* state only — it does not depend on
 * which panel is open:
 *   - visibility (checkedNodes)
 *   - collection-level filters (the collection form draft, which equals the
 *     saved values until edited)
 *   - every object's saved per-object filter, applied for all objects at once
 *   - plus a live override for the one object being actively edited, so its
 *     unsaved changes preview on the map
 *
 * Because the saved values already cover the selected object until the user
 * edits it, merely opening or switching a panel produces the same set — no
 * recompute, no map churn, no blink.
 *
 * @param {Array}  features    - full feature set (data?.features from useCollectionData)
 * @param {object} filterValues - { collectionFilterValues, objectFilterValues } from useCollectionFilterValues
 */
const useFilteredFeatures = (features, filterValues) => {
	const checkedNodes = useCollectionStore(s => s.checkedNodes);
	const setFilteredFeatures = useCollectionStore(s => s.setFilteredFeatures);

	// Recompute and write filtered features in a single effect. The store skips
	// the writing when the resulting set is unchanged, so unaffected subscribers
	// and the map layer don't re-render.
	useEffect(() => {
		const filtered = filterFeatures(
			features,
			checkedNodes,
			filterValues.collectionFilterValues,
			filterValues.objectFilterValues
		);
		setFilteredFeatures(filtered);
	}, [
		features,
		checkedNodes,
		filterValues.collectionFilterValues,
		filterValues.objectFilterValues,
		setFilteredFeatures,
	]);
};

export default useFilteredFeatures;
